import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const positiveIntSchema = z.number().int().positive();
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const taxonomyVersionSchema = z.string().refine((value) => value.trim().length > 0, 'must not be blank');

export const similarCategoryAssignmentSchema = z.object({
  category_id: uuidSchema,
  taxonomy_version: taxonomyVersionSchema,
}).strict();

export const similarPreviewScopeSchema = z.object({
  kind: z.enum(['all_time', 'date_range']),
  date_from: dateSchema.optional(),
  date_to: dateSchema.optional(),
}).strict().superRefine((scope, context) => {
  if (scope.kind === 'all_time' && (scope.date_from !== undefined || scope.date_to !== undefined)) {
    context.addIssue({ code: 'custom', message: 'all_time scope cannot include dates' });
  }
  if (scope.kind === 'date_range' && (scope.date_from === undefined || scope.date_to === undefined)) {
    context.addIssue({ code: 'custom', message: 'date_range scope requires both dates' });
  }
  if (scope.date_from !== undefined && scope.date_to !== undefined && scope.date_from > scope.date_to) {
    context.addIssue({ code: 'custom', message: 'date_from must be on or before date_to' });
  }
});
export type SimilarPreviewScope = z.infer<typeof similarPreviewScopeSchema>;

export const similarPreviewChangesSchema = z.object({
  category: similarCategoryAssignmentSchema,
  business_state: z.enum(['business', 'personal']).optional(),
}).strict();

export const similarPreviewRequestSchema = z.object({
  expected_version: positiveIntSchema,
  changes: similarPreviewChangesSchema,
  scope: similarPreviewScopeSchema,
}).strict();
export type SimilarPreviewRequest = z.infer<typeof similarPreviewRequestSchema>;

const similarPreviewStateSchema = z.object({
  category: similarCategoryAssignmentSchema.nullable(),
  business_state: z.enum(['business', 'personal']),
}).strict();

export const similarMatchSchema = z.object({
  line_item_id: uuidSchema,
  version: positiveIntSchema,
  merchant: z.string().nullable(),
  posted_day: dateSchema,
  amount: z.string().regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/).refine((value) => value !== '-0.0000', 'negative zero is not canonical'),
  currency: z.string().regex(/^[A-Z]{3}$/),
  current: similarPreviewStateSchema,
  proposed: similarPreviewStateSchema,
}).strict();

export const similarPreviewDataSchema = z.object({
  preview_id: uuidSchema,
  source_line_item_id: uuidSchema,
  merchant_display: z.string(),
  scope: similarPreviewScopeSchema,
  match_count: z.number().int().nonnegative(),
  matches: z.array(similarMatchSchema),
  excluded: z.object({
    user_override: z.number().int().nonnegative(),
    split: z.number().int().nonnegative(),
    not_editable: z.number().int().nonnegative(),
  }).strict(),
  expires_at: z.string().datetime({ offset: true }),
}).strict();
export type SimilarPreviewData = z.infer<typeof similarPreviewDataSchema>;

export const similarPreviewResponseMetaSchema = z.object({
  coverage: z.enum(['complete', 'partial']),
}).strict();
export type SimilarPreviewResponseMeta = z.infer<typeof similarPreviewResponseMetaSchema>;

export interface SimilarPreviewInput {
  lineItemId: string;
  expectedVersion: number;
  changes: z.infer<typeof similarPreviewChangesSchema>;
  scope: SimilarPreviewScope;
}

export interface SimilarPreviewResult {
  data: SimilarPreviewData;
  meta: SimilarPreviewResponseMeta;
}

export function useSimilarPreview() {
  return useMutation<SimilarPreviewResult, Error, SimilarPreviewInput>({
    mutationFn: async ({ lineItemId, expectedVersion, changes, scope }) => {
      const body = similarPreviewRequestSchema.parse({
        expected_version: expectedVersion,
        changes,
        scope,
      });
      const response = await client.post(`/api/line-items/${lineItemId}/similar-preview`, body);
      const parsed = parseEnvelope(similarPreviewDataSchema, response.data);
      return {
        data: parsed.data,
        meta: similarPreviewResponseMetaSchema.parse(parsed.meta ?? {}),
      };
    },
    retry: false,
  });
}

